import styles from './AddVolumeTrendAlert.module.scss'
import { NumberInput } from 'src/components/inputs/NumberInput/NumberInput'
import { FC, useEffect, useMemo, useState } from 'react'
import { Button, Preloader } from 'src/components';
import { formatToLocaleNumber, formatWithSpaces } from 'src/utils';
import { useTranslation } from 'react-i18next';
import { Warning } from 'src/assets/icons';

interface Props {
  percentage: string;
  onPercentageChange: (value: string) => void;
  onValueModified: (modified: boolean) => void;
  initialValue: string | number;
  currentVolume: number | null;
  isLoadingCurrentVolume: boolean;
  type: 'consistent' | 'oneTime';
  onTypeChange: (value: 'consistent' | 'oneTime') => void;
  targetVolume: number | null;
  onTargetVolumeChange: (value: number | null) => void;
  onAbsoluteNumberChange: (value: number | null) => void;
}

export const AddVolumeTrendAlert: FC<Props> = ({ 
  percentage, 
  onPercentageChange, 
  onValueModified, 
  initialValue, 
  currentVolume, 
  isLoadingCurrentVolume,
  type,
  onTypeChange,
  targetVolume,
  onTargetVolumeChange,
  onAbsoluteNumberChange,
}) => {
  const { t } = useTranslation();
  
  const sign = percentage?.startsWith('-') ? '-' : '+';
  const inputValue = percentage?.replace(/[+-]/, '');

  const [initialType, setInitialType] = useState(type);

  useEffect(() => {
    const s = percentage?.startsWith('-') ? '-' : '+';
    const v = percentage?.replace(/[+-]/, '');
    if (percentage !== s + v) {
      onPercentageChange(s + v);
    }
  }, [percentage, onPercentageChange]);

  useEffect(() => {
    if (type === 'oneTime') {
      onAbsoluteNumberChange(targetVolume);
    }
  }, [targetVolume, type, onAbsoluteNumberChange]);


  const calculatedTargetVolume = useMemo(() => {
    if (type === 'oneTime') {
      return targetVolume;
    }

    if (currentVolume) {
    const percentageValue = parseFloat(inputValue) || 0;
    const deviation = currentVolume * (percentageValue / 100);
    return currentVolume + (sign === '-' ? -deviation : deviation);
    }
    return null;
  }, [sign, inputValue, currentVolume, type, targetVolume]);

  useEffect(() => {
    if (type === 'consistent') {
      const cleanValue = percentage.replace(/[+-]/, '');
      const isZeroOrEmpty = cleanValue === '' || Number(cleanValue) === 0;
      onValueModified(!isZeroOrEmpty && percentage !== initialValue && currentVolume !== null);
    } else {
      onValueModified(targetVolume !== null && targetVolume !== initialValue);
    }
  }, [percentage, initialValue, onValueModified, type, initialType, targetVolume, currentVolume]);

  const handleSignChange = (newSign: string) => {
    let numeric = parseFloat(inputValue);
    if (isNaN(numeric)) numeric = 0;
  
    if (newSign === '-' && numeric > 100) {
      numeric = 100;
    }
  
    onPercentageChange(newSign + numeric.toString());
  };

  const handlePercentageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;

    value = value.replace(/[^0-9.]/g, '');

    const parts = value.split('.');
    if (parts.length > 2) return;

    if (parts[1] && parts[1].length > 2) return;

    let numeric = parseFloat(value);
    if (isNaN(numeric)) {
      onPercentageChange(sign + '');
      return;
    }

    if (sign === '-' && numeric > 100) {
      numeric = 100;
    } 

    onPercentageChange(sign + numeric.toString());
  };

  const calculatePercentageFromAbsolute = (abs: number | null, current: number | null) => {
    if (abs === null || current === null || current === 0) return '';
    const diff = abs - current;
    const percent = (Math.abs(diff) / current) * 100;
    const formatted = percent.toFixed(2).replace(/\.?0+$/, '');
    return (diff < 0 ? '-' : '+') + formatted;
  };

  const handleAlertTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newType = e.target.value as 'consistent' | 'oneTime';
    if (newType === type) return;

    if (newType === 'oneTime') {
      const percentageValue = parseFloat(inputValue) || 0;
      if (currentVolume !== null) {
        const deviation = currentVolume * (percentageValue / 100);
        const newTarget = currentVolume + (sign === '-' ? -deviation : deviation);
        onTargetVolumeChange(Math.round(newTarget));
      }
      onPercentageChange('');
    } else {
      const abs = targetVolume;
      const newPercentage = calculatePercentageFromAbsolute(abs, currentVolume);
      onPercentageChange(newPercentage);
      onTargetVolumeChange(abs ?? null);
    }
    onTypeChange(newType);
  };

  const handleAbsoluteNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value.replace(/\s+/g, '');

    if (!/^\d*$/.test(rawValue)) return;

    if (rawValue === '') {
      onTargetVolumeChange(null);
    } else {
      onTargetVolumeChange(Number(rawValue));
    }
  };

  return (
    <div className={styles.container}>
      <span className={styles.setAlertBy}>
        Set alert by: 
        <label className={styles.radioWrapper}>
          <input
            type="radio"
            name="alertType"
            value="consistent"
            checked={type === 'consistent'}
            onChange={handleAlertTypeChange}
            className={styles.radioInput}
          />
          <span className={styles.customRadio}></span>
          Percentage
        </label>
        <label className={styles.radioWrapper}>
          <input
            type="radio"
            name="alertType"
            value="oneTime"
            checked={type === 'oneTime'}
            onChange={handleAlertTypeChange}
            className={styles.radioInput}
          />
          <span className={styles.customRadio}></span>
          Absolute number
        </label>     
      </span>
      <div className={styles.items}>
      {type === 'consistent' ? ( 
        <div className={styles.numberInput}>
          <NumberInput
            label="Percentage for deviation"
            sign={sign}
            unit="%"
            value={inputValue}
            placeholder="0"
            onChange={handlePercentageChange}
            min={0}
            width="100px"
            disabled={currentVolume === null}
            onKeyDown={(e) => {
              if ((sign === '-' && parseFloat(inputValue) === 100 && e.key === '.' || e.key === ',') || e.key === '+' || e.key === '-') {
                e.preventDefault();
              }
            }}
          />
          <div className={styles.buttons}>
            <Button
              type="button"
              content="+"
              buttonType="secondary"
              buttonStyle="filled"
              onClick={() => handleSignChange('+')}
              disabled={sign === '+'}
              aria-label="Set positive deviation"
            />
            <Button
              type="button"
              content="-"
              buttonType="secondary"
              buttonStyle="filled"
              onClick={() => handleSignChange('-')}
              disabled={sign === '-'}
              aria-label="Set negative deviation"
            />
          </div>
        </div>
      ) : (
        <div className={styles.numberInput}>
          <NumberInput
            label="Absolute number for deviation"
            type="text"
            value={targetVolume !== null ? formatWithSpaces(targetVolume) : ''}
            placeholder="0"
            onChange={handleAbsoluteNumberChange}
            width="200px"
            onKeyDown={(e) => {
              if (!/[0-9]/.test(e.key) && 
              e.key !== 'Backspace' && 
              e.key !== 'Delete' && 
              e.key !== 'ArrowLeft' && 
              e.key !== 'ArrowRight') {
                e.preventDefault();
              }
            }}
          />
        </div>
      )}
        <div className={styles.volume}>
          <div className={styles.row}>
            <span className={styles.label}>Current volume</span>
            <span className={styles.colon}>:</span>
            <span className={styles.value}>
              {isLoadingCurrentVolume ? (
                <Preloader type="bar" />
              ) : (
                currentVolume !== null ? formatToLocaleNumber(currentVolume) : 0
              )}
            </span>
          </div>
          <div className={styles.row}>
            <span className={styles.label}>Target volume</span>
            <span className={styles.colon}>:</span>
            <span className={styles.value}>
              {calculatedTargetVolume !== null ? formatToLocaleNumber(calculatedTargetVolume) : 0}
            </span>
          </div>
        </div>
      </div>  
      
        {(currentVolume === null && type === 'consistent') && (
          <div className={styles.warningContainer}>
            <span className={styles.warningIcon}><Warning /></span>
            <span className={styles.warningText}>Percentage alerts aren't available for 0 current volume. Please switch to absolute number type instead.</span>
          </div>
        )}  
      <div className={styles.confirm}>
        <span>{t("component.modal.trend_alerts.volume_email_confirmation")}</span>
      </div>
  </div>
  );
}